import { Link, useLocation, useNavigate } from "react-router-dom";
import { Instagram, Linkedin, Twitter, MessageCircle } from "lucide-react";
import { useT } from "@/context/LanguageContext";
import EmailLink from "@/components/common/EmailLink";

export default function Footer({ settings }) {
  const t = useT();
  const brand = settings?.brand_name || "Gomes Digital Lab";
  const wa = (settings?.whatsapp_number || "").replace(/\D/g, "");
  const location = useLocation();
  const navigate = useNavigate();

  const goToSection = (e, id) => {
    e.preventDefault();
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate({ pathname: "/", hash: `#${id}` });
    }
  };

  const goHome = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <footer className="relative bg-ivory border-t border-line mt-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="font-display text-4xl md:text-5xl tracking-tight text-ink leading-none">
            {brand.split(" ")[0]}{" "}
            <span className="text-graphite italic font-light">{brand.split(" ").slice(1).join(" ")}</span>
          </div>
          <p className="mt-6 text-sm text-graphite max-w-md leading-relaxed">
            {settings?.tagline || "Private Digital Systems. Built for Performance."}
          </p>
          <div className="rule-gold-left my-8 max-w-[8rem]" />
          <p className="text-xs text-mute">
            {settings?.footer_text || `© ${new Date().getFullYear()} ${brand}. All rights reserved.`}
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow-light mb-5">{t("footer.navigate")}</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/" onClick={goHome} className="text-graphite hover:text-ink" data-testid="footer-home">{t("nav.home")}</Link></li>
            <li><Link to="/portfolio" className="text-graphite hover:text-ink" data-testid="footer-portfolio">{t("nav.portfolio")}</Link></li>
            <li><Link to="/insights" className="text-graphite hover:text-ink" data-testid="footer-insights">{t("footer.insights")}</Link></li>
            <li><a href="/#services" onClick={(e) => goToSection(e, "services")} className="text-graphite hover:text-ink" data-testid="footer-services">{t("nav.services")}</a></li>
            <li><a href="/#about" onClick={(e) => goToSection(e, "about")} className="text-graphite hover:text-ink" data-testid="footer-about">{t("nav.about")}</a></li>
            <li><a href="/#contact" onClick={(e) => goToSection(e, "contact")} className="text-graphite hover:text-ink" data-testid="footer-contact">{t("nav.contact")}</a></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="eyebrow-light mb-5">{t("footer.privateChannel")}</div>
          <div className="flex flex-col gap-4 text-sm">
            {settings?.contact_email && (
              <EmailLink
                email={settings.contact_email}
                label="General"
                testid="footer-email-general"
              />
            )}
            {settings?.sales_email && (
              <EmailLink
                email={settings.sales_email}
                label="Sales"
                testid="footer-email-sales"
              />
            )}
            {settings?.support_email && (
              <EmailLink
                email={settings.support_email}
                label="Support"
                testid="footer-email-support"
              />
            )}
            {settings?.whatsapp_number && (
              <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-graphite hover:text-ink" data-testid="footer-whatsapp">
                <MessageCircle size={14} className="text-gold-light" /> {settings.whatsapp_number}
              </a>
            )}
          </div>
          <div className="flex items-center gap-3 mt-8">
            {settings?.instagram && (
              <a href={settings.instagram} target="_blank" rel="noreferrer" className="w-9 h-9 border border-line hover:border-gold-light text-graphite hover:text-gold-light flex items-center justify-center transition-colors"><Instagram size={14} /></a>
            )}
            {settings?.linkedin && (
              <a href={settings.linkedin} target="_blank" rel="noreferrer" className="w-9 h-9 border border-line hover:border-gold-light text-graphite hover:text-gold-light flex items-center justify-center transition-colors"><Linkedin size={14} /></a>
            )}
            {settings?.twitter && (
              <a href={settings.twitter} target="_blank" rel="noreferrer" className="w-9 h-9 border border-line hover:border-gold-light text-graphite hover:text-gold-light flex items-center justify-center transition-colors"><Twitter size={14} /></a>
            )}
          </div>
        </div>
      </div>

      {settings?.whatsapp_number && (
        <a
          href={`https://wa.me/${wa}`}
          target="_blank"
          rel="noreferrer"
          data-testid="floating-whatsapp"
          className="fixed bottom-6 left-6 z-40 w-14 h-14 rounded-full bg-paper border border-line text-ink flex items-center justify-center shadow-[0_20px_40px_-10px_rgba(201,163,90,0.45)] hover:scale-105 hover:border-gold-light hover:text-gold-light transition-all"
          aria-label="WhatsApp"
          style={{ color: "#0E0E10" }}
        >
          <MessageCircle size={22} />
        </a>
      )}
    </footer>
  );
}
